import { useQuery } from "@apollo/client"
import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import * as z from "zod"
import { gql } from "~/__generated__"
import { cn } from "~/common/cn"
import { formatMonthlyPrice } from "~/common/format-monthly-price"
import { myLettermemeManagePath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { ScreenLoading } from "~/ui/screen-loading"
import { StripeConnectPendingBanner } from "~/stripe/stripe-connect-pending-banner"
import Text from "~/ui/typography"
import { useToast } from "~/ui/use-toast"

const monetizationScreenQuery = gql(/* GraphQL */ `
  query MyLettermemeMonetization($id: ID!) {
    stripeConfig {
      minSubscriptionPriceCents
    }
    viewer {
      id
      stripeAccountId
      stripeConnectReady
      stripeConnectRequirementsPending
    }
    lettermeme(id: $id) {
      id
      name
      subscriptionPriceCents
      subscriptionCurrency
    }
  }
`)

const stripeConnectOnboardingLinkCreateMutation = gql(/* GraphQL */ `
  mutation StripeConnectOnboardingLinkCreateFromMonetization {
    stripeConnectOnboardingLinkCreate(input: {}) {
      url
    }
  }
`)

const stripeConnectLoginLinkCreateMutation = gql(/* GraphQL */ `
  mutation StripeConnectLoginLinkCreateFromMonetization {
    stripeConnectLoginLinkCreate(input: {}) {
      url
    }
  }
`)

const stripeConnectAccountRefreshMutation = gql(/* GraphQL */ `
  mutation StripeConnectAccountRefreshFromMonetization {
    stripeConnectAccountRefresh(input: {}) {
      stripeConnectReady
    }
  }
`)

const lettermemeMonetizationUpdateMutation = gql(/* GraphQL */ `
  mutation LettermemeMonetizationUpdateFromMonetizationScreen(
    $input: LettermemeMonetizationUpdateInput!
  ) {
    lettermemeMonetizationUpdate(input: $input) {
      lettermeme {
        id
        monetizationEnabled
        subscriptionPriceCents
        subscriptionCurrency
      }
    }
  }
`)

export const MyLettermemeMonetizationScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()
  const [redirecting, setRedirecting] = useState(false)
  const [priceDraft, setPriceDraft] = useState("")

  const { data, error, loading } = useQuery(monetizationScreenQuery, {
    variables: { id: id! },
    fetchPolicy: "cache-first",
  })

  const lettermeme = data?.lettermeme
  const viewer = data?.viewer

  useDocumentTitle(lettermeme ? `Monetization - ${lettermeme.name}` : "Monetization")

  const [startStripeOnboarding, { loading: onboardingLoading }] = useSafeMutation(
    stripeConnectOnboardingLinkCreateMutation,
    {
      onCompleted: (data) => {
        window.setTimeout(() => {
          window.location.assign(data.stripeConnectOnboardingLinkCreate.url)
        }, 100)
      },
      onError: (error) => {
        setRedirecting(false)
        toast({ title: "Error", description: error.message, variant: "destructive" })
      },
    }
  )

  const [openStripeDashboard, { loading: dashboardLoading }] = useSafeMutation(
    stripeConnectLoginLinkCreateMutation,
    {
      onCompleted: (data) => {
        window.setTimeout(() => {
          window.location.assign(data.stripeConnectLoginLinkCreate.url)
        }, 100)
      },
      onError: (error) => {
        setRedirecting(false)
        toast({ title: "Error", description: error.message, variant: "destructive" })
      },
    }
  )

  const [refreshStripeConnectStatus, { loading: refreshingStripeStatus }] = useSafeMutation(
    stripeConnectAccountRefreshMutation,
    {
      onCompleted: () => {
        toast({ title: "Refreshed", description: "Stripe status updated." })
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" })
      },
      refetchQueries: ["MyLettermemeMonetization"],
    }
  )

  const [updateMonetization, { loading: savingPrice }] = useSafeMutation(
    lettermemeMonetizationUpdateMutation,
    {
      onCompleted: () => {
        toast({ title: "Saved", description: "Subscription price set." })
        setPriceDraft("")
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" })
      },
      refetchQueries: ["MyLettermemeMonetization"],
    }
  )

  if (error) return <GraphqlError error={error} />
  if (loading) return <ScreenLoading />

  if (!lettermeme || !viewer || !data?.stripeConfig) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Text as="h1" className="mb-4 text-2xl font-bold">
          Monetization
        </Text>
        <Text className="text-gray-500">This lettermeme could not be loaded.</Text>
      </div>
    )
  }

  const stripeConnectReady = viewer.stripeConnectReady ?? false
  const hasStripeAccount = Boolean(viewer.stripeAccountId)
  const stripeConnectRequirementsPending = viewer.stripeConnectRequirementsPending ?? false
  const stripeLoading = onboardingLoading || dashboardLoading
  const stripeConfig = data.stripeConfig
  const minSubscriptionPriceCents = stripeConfig.minSubscriptionPriceCents
  const minSubscriptionPriceDollars = minSubscriptionPriceCents / 100
  const minSubscriptionPriceLabel = `$${minSubscriptionPriceDollars.toFixed(2)}/month`
  const subscriptionPriceSchema = z
    .string()
    .trim()
    .refine((value) => value.length > 0, {
      message: `Enter a dollar amount (e.g. ${minSubscriptionPriceDollars.toFixed(2)}).`,
    })
    .transform((value) => Number.parseFloat(value))
    .refine((value) => Number.isFinite(value) && value > 0, {
      message: `Enter a valid dollar amount (e.g. ${minSubscriptionPriceDollars.toFixed(2)}).`,
    })
    .refine((value) => value >= minSubscriptionPriceDollars, {
      message: `Minimum price is ${minSubscriptionPriceLabel}.`,
    })

  if (redirecting) {
    return <ScreenLoading label="Connecting to Stripe..." delayMs={0} />
  }

  const hasPrice = Boolean(lettermeme.subscriptionPriceCents)
  const canSetPrice = stripeConnectReady && !hasPrice

  const connectedLabel = stripeConnectReady ? "Connected!" : "Not connected"

  const payoutsButtonLabel = stripeLoading
    ? "Loading..."
    : stripeConnectReady
      ? "manage"
      : !hasStripeAccount
        ? "setup"
        : stripeConnectRequirementsPending
          ? "continue payout setup"
          : "refresh status"

  return (
    <div className="mx-auto max-w-3xl p-8">
      <div className="mb-8">
        <Link to={myLettermemeManagePath({ id: lettermeme.id })} className="text-gray-400">
          ← back to lettermeme settings
        </Link>
        <Text as="h1" className="mt-4 text-2xl font-bold">
          Monetization
        </Text>
      </div>

      <div className="mx-auto mt-12 max-w-xl space-y-6">
        <StripeConnectPendingBanner
          hasStripeAccount={hasStripeAccount}
          stripeConnectReady={stripeConnectReady}
          stripeConnectRequirementsPending={stripeConnectRequirementsPending}
        />

        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4">
          <div className="text-gray-200">Payout Account</div>
          <div className="text-gray-400">{connectedLabel}</div>
          <Button
            type="button"
            variant="outline"
            disabled={stripeLoading || refreshingStripeStatus || redirecting}
            onClick={() => {
              if (stripeConnectReady) {
                setRedirecting(true)
                openStripeDashboard()
                return
              }
              if (hasStripeAccount && !stripeConnectRequirementsPending) {
                refreshStripeConnectStatus()
                return
              }
              setRedirecting(true)
              startStripeOnboarding()
            }}
          >
            {payoutsButtonLabel}
          </Button>
        </div>

        <div className="grid grid-cols-[1fr_auto] items-center gap-4">
          <div className="text-gray-200">Monthly Subscription Price</div>
          {hasPrice ? (
            <div className="text-gray-200">
              {formatMonthlyPrice(
                lettermeme.subscriptionPriceCents,
                lettermeme.subscriptionCurrency
              )}
            </div>
          ) : (
            <div
              className={cn(
                "flex h-10 w-36 items-center rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                (!canSetPrice || savingPrice) && "cursor-not-allowed opacity-60"
              )}
            >
              <input
                value={priceDraft}
                onChange={(e) => setPriceDraft(e.target.value)}
                placeholder={minSubscriptionPriceDollars.toFixed(2)}
                inputMode="decimal"
                disabled={!canSetPrice || savingPrice}
                className="h-full w-full flex-1 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed"
              />
              <span className="pr-3 text-sm text-gray-400">
                {lettermeme.subscriptionCurrency.toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {!stripeConnectReady && !hasPrice ? (
          <div className="text-sm text-gray-500">
            Connect your payout account before setting a price.
          </div>
        ) : null}

        {hasPrice ? (
          <div className="text-sm text-gray-500">Price is set and can’t be edited.</div>
        ) : canSetPrice ? (
          <div className="text-sm text-gray-500">Minimum {minSubscriptionPriceLabel}.</div>
        ) : null}

        {!hasPrice && canSetPrice ? (
          <div className="pt-2 text-center">
            <Button
              type="button"
              variant="outline"
              disabled={savingPrice}
              onClick={() => {
                const parsedPrice = subscriptionPriceSchema.safeParse(priceDraft)
                if (!parsedPrice.success) {
                  toast({
                    title: "Invalid price",
                    description:
                      parsedPrice.error.issues[0]?.message ||
                      "Enter a valid dollar amount (e.g. 5.00).",
                    variant: "destructive",
                  })
                  return
                }

                const dollars = parsedPrice.data

                updateMonetization({
                  variables: {
                    input: {
                      lettermemeId: lettermeme.id,
                      monetizationEnabled: true,
                      subscriptionPriceCents: Math.round(dollars * 100),
                      subscriptionCurrency: lettermeme.subscriptionCurrency,
                    },
                  },
                })
              }}
            >
              {savingPrice ? "Saving..." : "save"}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
